import { memo } from 'react';
import {
  type EdgeProps,
  type Edge,
  getBezierPath,
  EdgeLabelRenderer,
  BaseEdge,
} from '@xyflow/react';

export interface ConnectionEdgeData extends Record<string, unknown> {
  id: string;
  wormholeType: string | null;
  massStatus: 'fresh' | 'reduced' | 'critical';
  timeStatus: 'stable' | 'eol';
  sourceSig: string | null;
  targetSig: string | null;
  eolAt: number | null;
}

export type ConnectionEdge = Edge<ConnectionEdgeData, 'connection'>;

// Mass status colors
const massColors = {
  fresh: '#22c55e', // green
  reduced: '#eab308', // yellow
  critical: '#ef4444', // red
};

// Mass status stroke widths
const massWidths = {
  fresh: 3,
  reduced: 2,
  critical: 1,
};

function ConnectionEdgeComponent({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
}: EdgeProps<ConnectionEdge>) {
  const edgeData = (data || {}) as ConnectionEdgeData;
  const { wormholeType, massStatus = 'fresh', timeStatus = 'stable', eolAt } = edgeData;

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Calculate time remaining for EOL
  const getEolCountdown = () => {
    if (!eolAt) return null;
    const remaining = eolAt - Date.now();
    if (remaining <= 0) return 'Expired';

    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const eolCountdown = timeStatus === 'eol' ? getEolCountdown() : null;

  // Edge color and style based on status
  const edgeColor = massColors[massStatus];
  const strokeWidth = massWidths[massStatus];
  const strokeDasharray = timeStatus === 'eol' ? '5,5' : undefined;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          stroke: edgeColor,
          strokeWidth: selected ? strokeWidth + 1 : strokeWidth,
          strokeDasharray,
          opacity: timeStatus === 'eol' ? 0.7 : 1,
        }}
      />

      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
          }}
          className="nodrag nopan"
        >
          <div
            className={`
              flex flex-col items-center gap-0.5 rounded px-1.5 py-0.5 text-xs
              ${selected ? 'bg-gray-700' : 'bg-gray-800/80'}
              border border-gray-600
            `}
          >
            {/* Wormhole type */}
            {wormholeType && (
              <span className="font-semibold text-white">{wormholeType}</span>
            )}

            {/* Mass indicator */}
            <div className="flex items-center gap-1">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: edgeColor }}
                title={`Mass: ${massStatus}`}
              />
              {massStatus !== 'fresh' && (
                <span className="text-gray-400 capitalize">{massStatus}</span>
              )}
            </div>

            {/* EOL countdown */}
            {eolCountdown && (
              <span className="text-red-400 text-[10px]">
                EOL: {eolCountdown}
              </span>
            )}
          </div>
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

export default memo(ConnectionEdgeComponent);
